import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export type SettingsModule = 'biometric-settings' | 'goal-settings' | 'nutrition-settings';

interface AllSettingsContextType {
  currentModule: SettingsModule;
  setCurrentModule: (module: SettingsModule) => void;
  navigateToNextModule: () => void;
  isLastModule: boolean;
}

const AllSettingsContext = createContext<AllSettingsContextType | undefined>(undefined);

interface AllSettingsProviderProps {
  children: ReactNode;
}

export const AllSettingsProvider: React.FC<AllSettingsProviderProps> = ({ children }) => {
  const [currentModule, setCurrentModule] = useState<SettingsModule>('biometric-settings');
  const navigate = useNavigate();

  const moduleOrder: SettingsModule[] = [
    'biometric-settings',
    'goal-settings',
    'nutrition-settings',
  ];

  const navigateToNextModule = () => {
    const currentIndex = moduleOrder.indexOf(currentModule);
    const nextIndex = currentIndex + 1;

    if (nextIndex < moduleOrder.length) {
      const nextModule = moduleOrder[nextIndex];
      setCurrentModule(nextModule);

      // Navigate to the first step of the next module
      switch (nextModule) {
        case 'goal-settings':
          navigate('/all-settings/goal-settings/weight');
          break;
        case 'nutrition-settings':
          navigate('/all-settings/nutrition-settings/diet-type');
          break;
        default:
          break;
      }
    } else {
      // All modules completed - redirect to home or wherever needed
      navigate('/');
    }
  };

  const isLastModule = currentModule === 'nutrition-settings';

  return (
    <AllSettingsContext.Provider
      value={{
        currentModule,
        setCurrentModule,
        navigateToNextModule,
        isLastModule,
      }}
    >
      {children}
    </AllSettingsContext.Provider>
  );
};

export const useAllSettings = () => {
  const context = useContext(AllSettingsContext);
  if (context === undefined) {
    throw new Error('useAllSettings must be used within an AllSettingsProvider');
  }
  return context;
};
